import type { VehicleRow, SpecRow } from "@/lib/vehicle-queries";
import type { DataTypeKey } from "@/lib/vehicle-data";
import { towingBenchmark, cruiseMpg75, usableTankRange } from "@/lib/proprietary-metrics";

/**
 * Conversational H1 + blunt shop-mechanic BLUF for the single-spec template.
 * Written the way a buyer asks the question out loud, so answer engines can
 * match the query text directly.
 */

export interface SpecCopyInput {
  fullName: string;
  latestYear: number | null;
  vehicles: VehicleRow[];
  specs: SpecRow[];
}

function named(input: SpecCopyInput) {
  return [input.latestYear, input.fullName].filter(Boolean).join(" ");
}

function bestTow(specs: SpecRow[]): number | null {
  return specs.reduce<number | null>(
    (b, s) => (s.towing_capacity_lbs && (!b || s.towing_capacity_lbs > b) ? s.towing_capacity_lbs : b),
    null,
  );
}

function bestHwy(vehicles: VehicleRow[]): VehicleRow | null {
  return vehicles.reduce<VehicleRow | null>(
    (b, v) => (v.mpg_hwy && (!b || (b.mpg_hwy ?? 0) < v.mpg_hwy) ? v : b),
    null,
  );
}

export function specHeadline(dataType: DataTypeKey, input: SpecCopyInput): string | null {
  const n = named(input);
  switch (dataType) {
    case "towing-capacity":
      return `How much weight can a ${n} actually tow?`;
    case "mpg":
      return `What is the real-world gas mileage of a ${n}?`;
    case "fuel-tank-capacity":
      return `How big is the gas tank on a ${n}?`;
    case "bed-dimensions":
      return `What size is the bed on a ${n}?`;
    case "dimensions":
      return `How much cargo space and room does a ${n} really have?`;
    case "maintenance-schedule":
      return `When should you service your ${n}?`;
    default:
      return null;
  }
}

/** Two blunt sentences, built from our own adjusted numbers. */
export function specBluf(dataType: DataTypeKey, input: SpecCopyInput): string | null {
  const n = named(input);
  const vehicleClass = input.vehicles[0]?.vehicle_class ?? null;

  if (dataType === "towing-capacity") {
    const b = towingBenchmark(bestTow(input.specs));
    if (!b) return null;
    return `While the official spec sheet claims ${b.officialLbs.toLocaleString()} lbs, our 80% mechanic safety margin puts a comfortable working limit on the ${n} at ${b.safeLbs.toLocaleString()} lbs — drop to ${b.gradeLbs.toLocaleString()} lbs for long mountain grades. Load past that and you cook the transmission fluid and the cooling system pays for it, so read the structural breakdown below before you hook up.`;
  }

  if (dataType === "mpg") {
    /* hwy MPG scoped to latest model year */ const v = bestHwy(input.vehicles.filter((x) => !input.latestYear || x.year === input.latestYear));
    const cruise = cruiseMpg75(v?.mpg_hwy ?? null, vehicleClass);
    if (!v?.mpg_hwy || !cruise) return null;
    return `The EPA says the ${n} does ${v.mpg_hwy} MPG highway, but that test never holds interstate speed. Our Real-World Speed Adjuster puts it near ${cruise} MPG at a steady 75 mph, because drag climbs with the square of your speed.`;
  }

  if (dataType === "fuel-tank-capacity") {
    const gal = input.specs.reduce<number | null>(
      (b, s) => (s.fuel_tank_gal && (!b || s.fuel_tank_gal > b) ? s.fuel_tank_gal : b),
      null,
    );
    const mpg = input.vehicles.reduce<number | null>(
      (b, v) => (v.mpg_combined && (!b || v.mpg_combined > b) ? v.mpg_combined : b),
      null,
    );
    const range = usableTankRange(gal, mpg);
    if (!gal) return null;
    return `The ${n} holds ${gal} gallons on paper, but the last tenth of a tank is pump pickup and sediment — plan on ${range ? `${range.usableGallons} usable gallons` : "about 90% of that"}.${
      range ? ` That is roughly ${range.miles.toLocaleString()} real miles between fill-ups at the combined rating.` : ""
    }`;
  }

  return null;
}
